(() => {
  const configElement = document.getElementById('FreeCutterConfig');
  if (!configElement) return;

  const config = JSON.parse(configElement.textContent);
  const strings = Object.fromEntries(Object.entries(config.strings).map(([key, value]) => [key, value || '']));
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  const sameCode = (a, b) => a.trim().toUpperCase() === b.trim().toUpperCase();
  const isFreeCutterCode = (code) => sameCode(code, config.code);
  const codesOf = (cart) => (cart.discount_codes || []).map((discount) => discount.code);
  const findCode = (cart, code) => (cart.discount_codes || []).find((discount) => sameCode(discount.code, code));
  const cutterLine = (cart) => cart.items.find((item) => item.variant_id === config.cutterVariantId);

  // Remembered in a cookie: it works wherever the cart itself works, unlike blockable browser storage.
  const addedCookie = (cart) => `free_cutter_added=${encodeURIComponent(cart.token)}`;
  const wasAutoAdded = (cart) => document.cookie.split('; ').includes(addedCookie(cart));
  const rememberAutoAdd = (cart) => {
    document.cookie = `${addedCookie(cart)}; path=/; max-age=2592000; samesite=lax`;
  };

  // The welcome email's button goes through /discount/CODE, which sets this session cookie. Open the cart once per
  // visit so the shopper sees the free cutter even when everything was already in the cart.
  const arrivedWithCode = () =>
    document.cookie.split('; ').some((c) => c.startsWith('discount_code=') && sameCode(decodeURIComponent(c.slice(14)), config.code));
  const cartShownThisVisit = () => document.cookie.split('; ').includes('free_cutter_shown=1');
  const rememberCartShown = () => {
    document.cookie = 'free_cutter_shown=1; path=/; samesite=lax';
  };

  const request = async (path, body) => {
    const options = { headers: { Accept: 'application/json' } };
    if (body !== undefined) {
      options.method = 'POST';
      options.headers['Content-Type'] = 'application/json';
      options.body = JSON.stringify(body);
    }
    const response = await fetch(`${root}${path}`, options);
    if (!response.ok) throw new Error(`${path} returned ${response.status}`);
    return response.json();
  };

  const getCart = () => request('cart.js');
  const setCodes = (codes) => request('cart/update.js', { discount: codes.join(',') });
  const addLines = (items) => request('cart/add.js', { items });
  const removeLine = (key) => request('cart/change.js', { id: key, quantity: 0 });

  // Re-reads the cart first: /cart/add.js raises the quantity of a line that's already there.
  const addCutter = async () => {
    let cart = await getCart();
    if (!cutterLine(cart)) {
      await addLines([{ id: config.cutterVariantId, quantity: 1 }]);
      cart = await getCart();
    }
    rememberAutoAdd(cart);
    return cart;
  };

  const whenDefined = (name) =>
    Promise.race([customElements.whenDefined(name), new Promise((resolve) => setTimeout(resolve, 3000))]);

  // Shows the new cart. Returns true when that means reloading the page.
  const refreshCart = async (cart) => {
    if (document.getElementById('main-cart-items')) {
      window.location.reload();
      return true;
    }

    const drawer = document.querySelector('cart-drawer');
    if (drawer) await whenDefined('cart-drawer');
    const sectionIds = drawer && typeof drawer.renderContents === 'function' ? 'cart-drawer,cart-icon-bubble' : 'cart-icon-bubble';
    const sections = await fetch(`${root}?sections=${sectionIds}`).then((response) => response.json());

    if (sectionIds.startsWith('cart-drawer')) {
      drawer.classList.toggle('is-empty', cart.item_count === 0);
      drawer.renderContents({ sections });
    } else {
      const bubble = document.getElementById('cart-icon-bubble');
      const html = new DOMParser().parseFromString(sections['cart-icon-bubble'], 'text/html');
      const source = html.querySelector('.shopify-section');
      if (bubble && source) bubble.innerHTML = source.innerHTML;
    }
    return false;
  };

  // Drops ?free_cutter=kit, and ?discount=... once the cart already has those codes, so a reload can't
  // re-add the kit or re-apply a code the shopper has since removed. Returns whether the kit was asked for.
  const takeUrlParams = (cart) => {
    const params = new URLSearchParams(window.location.search);
    const wantsKit = params.get('free_cutter') === 'kit';
    params.delete('free_cutter');

    const urlCodes = (params.get('discount') || '').split(',').filter((code) => code.trim());
    if (urlCodes.length && urlCodes.every((code) => findCode(cart, code))) params.delete('discount');

    const query = params.toString();
    const url = `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`;
    if (url !== `${window.location.pathname}${window.location.search}${window.location.hash}`) {
      window.history.replaceState(window.history.state, '', url);
    }
    return wantsKit;
  };

  // The code can reach the cart from any link (/discount/CODE, ?discount=CODE). Put the cutter in once per cart,
  // so a shopper who removes it isn't fighting the page. An empty cart also gets the kit: that's how the welcome
  // email's Apply discount button (fixed by Shopify to the homepage + code) and ?free_cutter=kit links land.
  const applyOfferOnLoad = async () => {
    let reloading = false;

    try {
      let cart = await getCart();
      const wantsKit = takeUrlParams(cart);
      if (wantsKit && !findCode(cart, config.code)) cart = await setCodes([...codesOf(cart), config.code]);

      const firstTime = wantsKit || !wasAutoAdded(cart);
      const codeOnCart = Boolean(findCode(cart, config.code));
      const addKit = codeOnCart && firstTime && cart.item_count === 0 && config.kitVariantId;
      const addFreeCutter = codeOnCart && firstTime && !cutterLine(cart);
      let changed = false;

      if (addKit) {
        await addLines([{ id: config.kitVariantId, quantity: 1 }]);
        changed = true;
      }
      if (addFreeCutter) {
        try {
          await addCutter();
          changed = true;
        } catch (error) {
          // Sold out or unavailable: don't try again on every page load.
          rememberAutoAdd(cart);
          console.error(error);
        }
      }

      if (changed) {
        rememberCartShown();
        reloading = await refreshCart(await getCart());
      } else if (codeOnCart && cart.item_count > 0 && arrivedWithCode() && !cartShownThisVisit()) {
        rememberCartShown();
        if (!document.getElementById('main-cart-items')) await refreshCart(cart);
      }
    } catch (error) {
      console.error(error);
    } finally {
      if (!reloading) document.documentElement.classList.remove('free-cutter-loading');
    }
  };

  class CartDiscount extends HTMLElement {
    constructor() {
      super();

      this.form = this.querySelector('.cart-discount__form');
      this.input = this.querySelector('input[name="discount"]');
      this.submitButton = this.querySelector('.cart-discount__apply');
      this.list = this.querySelector('.cart-discount__codes');
      this.message = this.querySelector('.cart-discount__message');

      this.form.addEventListener('submit', this.onSubmit);
      this.list.addEventListener('click', this.onRemove);
      this.message.addEventListener('click', this.onAddCutter);
    }

    cartUpdateUnsubscriber = undefined;
    loadRequest = 0;

    connectedCallback() {
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, () => this.load());
      }
      this.load();
    }

    disconnectedCallback() {
      if (this.cartUpdateUnsubscriber) this.cartUpdateUnsubscriber();
    }

    async load() {
      const loadRequest = ++this.loadRequest;
      try {
        await offerReady;
        const cart = await getCart();
        if (loadRequest === this.loadRequest) this.render(cart);
      } catch (error) {
        console.error(error);
      }
    }

    render(cart, error = '') {
      this.list.replaceChildren(...(cart.discount_codes || []).map((discount) => this.renderCode(discount.code)));
      this.message.replaceChildren();
      this.message.classList.toggle('cart-discount__message--error', Boolean(error));

      if (error) {
        this.message.textContent = error;
        return;
      }

      const freeCutterCode = findCode(cart, config.code);
      if (!freeCutterCode || freeCutterCode.applicable) return;

      if (cutterLine(cart)) {
        this.message.textContent = strings.qualify;
        return;
      }

      const addButton = document.createElement('button');
      addButton.type = 'button';
      addButton.className = 'cart-discount__add';
      addButton.dataset.freeCutterAdd = '';
      addButton.textContent = strings.addCutter;
      this.message.append(`${strings.missing} `, addButton);
    }

    renderCode(code) {
      const item = document.createElement('li');
      item.className = 'cart-discount__code';

      const label = document.createElement('span');
      label.textContent = code.toUpperCase();

      const removeButton = document.createElement('button');
      removeButton.type = 'button';
      removeButton.className = 'cart-discount__remove';
      removeButton.dataset.code = code;
      removeButton.setAttribute('aria-label', strings.remove.replace('[code]', () => code.toUpperCase()));
      removeButton.textContent = '×';

      item.append(label, removeButton);
      return item;
    }

    setBusy(busy) {
      this.busy = busy;
      this.submitButton.disabled = busy;
      this.setAttribute('aria-busy', String(busy));
    }

    async runAction(action) {
      if (this.busy) return;
      this.setBusy(true);
      this.loadRequest++;
      let reloading = false;

      try {
        await offerReady;
        reloading = await action();
      } catch (error) {
        console.error(error);
        this.message.classList.add('cart-discount__message--error');
        this.message.textContent = strings.error;
      } finally {
        if (!reloading) this.setBusy(false);
      }
    }

    onSubmit = (event) => {
      event.preventDefault();
      const typed = this.input.value.trim();
      if (!typed) return;

      this.runAction(async () => {
        const isFreeCutter = isFreeCutterCode(typed);
        const code = isFreeCutter ? config.code : typed;

        let cart = await getCart();
        const otherCodes = codesOf(cart).filter((existing) => !sameCode(existing, code));
        cart = await setCodes([...otherCodes, code]);
        if (isFreeCutter && !cutterLine(cart)) cart = await addCutter();

        const applied = findCode(cart, code);
        if (!isFreeCutter && !(applied && applied.applicable)) {
          this.render(await setCodes(otherCodes), strings.invalid.replace('[code]', () => typed.toUpperCase()));
          return false;
        }

        this.input.value = '';
        return refreshCart(cart);
      });
    };

    onRemove = (event) => {
      const button = event.target.closest('.cart-discount__remove');
      if (!button) return;
      const code = button.dataset.code;

      this.runAction(async () => {
        let cart = await getCart();
        cart = await setCodes(codesOf(cart).filter((existing) => !sameCode(existing, code)));

        const line = cutterLine(cart);
        if (isFreeCutterCode(code) && line && line.quantity === 1 && wasAutoAdded(cart)) {
          cart = await removeLine(line.key);
        }
        return refreshCart(cart);
      });
    };

    onAddCutter = (event) => {
      if (!event.target.closest('[data-free-cutter-add]')) return;
      this.runAction(async () => refreshCart(await addCutter()));
    };
  }

  const designMode = Boolean(window.Shopify && window.Shopify.designMode);
  if (designMode) document.documentElement.classList.remove('free-cutter-loading');
  const offerReady = designMode ? Promise.resolve() : applyOfferOnLoad();

  if (!customElements.get('cart-discount')) customElements.define('cart-discount', CartDiscount);
})();
